<template>
  <div class="tw-border-y tw-border-gray-700 tw-py-16">
    <h2 class="tw-text-5xl tw-mb-8">Delivery Information</h2>

    
    <div class="tw-mb-16 tw-italic">
      Deliveries will be completed by a third-party delivery service
    </div>

    <div class="tw-flex tw-flex-wrap tw-mb-8"> 
      <div class="tw-mb-10 tw-w-full">
        <div class="tw-pt-4 tw-outline tw-outline-0 tw-relative tw-border-b-2 focus-within:tw-border-orange-medium">
          <input 
            v-model="state.streetaddress"
            type="text" 
            name="streetaddress" 
            placeholder=" "
            :disabled="props.disabled"
            @keyup="streetaddressSet"
            class="tw-block tw-text-2xl tw-py-4 tw-w-full tw-border-0 tw-appearance-none focus:tw-outline-0 tw-bg-transparent tw-border-transparent focus:tw-border-transparent focus:tw-ring-0 tw-px-0" />
          <label for="streetaddress" class="tw-pointer-events-none tw-absolute tw-top-0 tw-py-4 tw--z-1 tw-duration-300 tw-origin-top-left">Street Address</label>
        </div>
      </div>

      <div class="tw-mb-10 tw-w-full">
        <div class="tw-pt-4 tw-outline tw-outline-0 tw-relative tw-border-b-2 focus-within:tw-border-orange-medium">
          <input 
            v-model="state.building"
            type="text" 
            name="building" 
            placeholder=" "
            :disabled="props.disabled"
            @keyup="buildingSet"
            class="tw-block tw-text-2xl tw-py-4 tw-w-full tw-border-0 tw-appearance-none focus:tw-outline-0 tw-bg-transparent tw-border-transparent focus:tw-border-transparent focus:tw-ring-0 tw-px-0" />
          <label for="building" class="tw-pointer-events-none tw-absolute tw-top-0 tw-py-4 tw--z-1 tw-duration-300 tw-origin-top-left">Building</label>
        </div>
      </div>

      <div class="tw-mb-10 tw-w-1/2 tw-pr-8">
        <div class="tw-pt-4 tw-outline tw-outline-0 tw-relative tw-border-b-2 focus-within:tw-border-orange-medium">
          <input 
            v-model="state.city"
            type="text" 
            name="city" 
            placeholder=" "
            :disabled="props.disabled"
            @keyup="citySet"
            class="tw-block tw-text-2xl tw-py-4 tw-w-full tw-border-0 tw-appearance-none focus:tw-outline-0 tw-bg-transparent tw-border-transparent focus:tw-border-transparent focus:tw-ring-0 tw-px-0" />
          <label for="city" class="tw-pointer-events-none tw-absolute tw-top-0 tw-py-4 tw--z-1 tw-duration-300 tw-origin-top-left">City</label>
        </div>
      </div>

      <div class="tw-mb-10 tw-w-1/2 tw-pl-8">
        <div class="tw-pt-4 tw-outline tw-outline-0 tw-relative tw-border-b-2 focus-within:tw-border-orange-medium">
          <input 
            v-model="state.zipcode"
            type="text" 
            name="zipcode" 
            placeholder=" "
            :disabled="props.disabled"
            @keyup="zipcodeSet"
            class="tw-block tw-text-2xl tw-py-4 tw-w-full tw-border-0 tw-appearance-none focus:tw-outline-0 tw-bg-transparent tw-border-transparent focus:tw-border-transparent focus:tw-ring-0 tw-px-0" />
          <label for="zipcode" class="tw-pointer-events-none tw-absolute tw-top-0 tw-py-4 tw--z-1 tw-duration-300 tw-origin-top-left">Zip</label>
        </div>
      </div>
    </div>

    <div class="tw-mb-10">
      <div class="tw-pt-4 tw-outline tw-outline-0 tw-relative tw-border-b-2 focus-within:tw-border-orange-medium">
        <input 
          v-model="state.specialinstructions"
          type="text" 
          name="specialinstructions" 
          placeholder=" "
          @keyup="specialinstructionsSet"
          class="tw-block tw-text-2xl tw-py-4 tw-w-full tw-border-0 tw-appearance-none focus:tw-outline-0 tw-bg-transparent tw-border-transparent focus:tw-border-transparent focus:tw-ring-0 tw-px-0" />
        <label for="specialinstructions" class="tw-pointer-events-none tw-absolute tw-top-0 tw-py-4 tw--z-1 tw-duration-300 tw-origin-top-left">Special Instructions (optional)</label>
      </div>
      <div class="tw-text-lg tw-mt-4">
        If you would like contactless delivery, please specify "Contactless Delivery" above along with instructions on where to leave your food. The driver will drop your food off without directly handing you the bags.
      </div>
    </div>

    <div>
      <TwButton
        cssClass="tw-bg-orange-medium tw-text-white tw-border tw-rounded-2xl tw-uppercase tw-text-xl tw-font-black tw-px-12 tw-mb-4 md:tw-mb-0 tw-py-4 tw-transition-all tw-duration-20"
        :disabled="false"
        :isLoading="props.submittingDeliveryInfo"
        :buttonText="'Set Delivery Information'"
        @click="deliveryInfoSubmit"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    reactive, computed, onMounted, ref, watch
  } from 'vue';

  import TwButton from '../../components/TwButton.vue';

  const props = defineProps<{ 
    streetaddress: string,
    building: string,
    city: string,
    zipcode: string,
    specialinstructions: string,
    disabled: boolean,
    submittingDeliveryInfo: boolean,
  }>();

  const state = reactive({
    streetaddress: '',
    building: '',
    city: '',
    zipcode: '',
    specialinstructions: '',
    initialized: false,
  })

  const emit = defineEmits(['setStreetaddress', 'setCity', 'setBuilding', 'setZipcode', 'setSpecialinstructions', 'submitDeliveryInfo'])

  onMounted(() => {
    setFromProps();
  })

  const setFromProps = () => {
    state.initialized = false;

    if (props.streetaddress) {
      state.streetaddress = props.streetaddress;
    } 

    if (props.building) {
      state.building = props.building;
    }

    if (props.city) {
      state.city = props.city;
    }

    if (props.zipcode) {
      state.zipcode = props.zipcode;
    }

    if (props.specialinstructions) {
      state.specialinstructions = props.specialinstructions;
    }

    state.initialized = true;
  }

  watch(() => props.streetaddress, (newAddress, oldAddress) => { 
    if (newAddress != state.streetaddress) {
      setFromProps();
    }
  })

  const streetaddressSet = ((event: Event) => {
    const target = event.target as HTMLInputElement;
    if (state.initialized) {
      emit('setStreetaddress', target.value)
    }
  })

  const citySet = ((event: Event) => {
    const target = event.target as HTMLInputElement;
    if (state.initialized) {
      emit('setCity', target.value)
    }
  })

  const buildingSet = ((event: Event) => {
    const target = event.target as HTMLInputElement;
    if (state.initialized) {
      emit('setBuilding', target.value)
    }
  })

  const zipcodeSet = ((event: Event) => {
    const target = event.target as HTMLInputElement;
    if (state.initialized) {
      emit('setZipcode', target.value)
    }
  })

  const specialinstructionsSet = ((event: Event) => {
    const target = event.target as HTMLInputElement;
    emit('setSpecialinstructions', target.value)
  })

  const deliveryInfoSubmit = (() => {
    emit('submitDeliveryInfo', true);
  })


</script>